import {
  IMeasureMetadata,
  MeasureFormatter,
  MeasureIcon,
  MeasureReducer,
} from '@principle-theorem/principle-core/interfaces';
import { TypeGuard, isObject } from '@principle-theorem/shared';
import {
  ComparableObject,
  ComparableValue,
  Group,
  Grouping,
  NaturallyOrderedValue,
} from 'crossfilter2';
import { isFunction, isString } from 'lodash';
import { IGroupedDimension } from './grouped-dimension';
import {
  ArrayDataAccessor,
  ColourAccessor,
  DataAccessor,
  IMeasure,
  IMeasureFilter,
  LabelAccessor,
  ReduceByFunction,
} from './measure';
import { IReportingQuery } from './querying';

export interface ICustomReportQueryMetadata {
  key: string;
  queryForValues: (values: string[]) => Omit<IReportingQuery, 'table'>;
}

export interface IChartBuilderData {
  plottedOverTime: boolean;
  accumulateOverTime: boolean;
  colourOverride: boolean;
  groupByDimension?: ICanGroupMeasuresProperty;
  measures: IMeasureBuilderData[];
  groupLimit?: number;
  otherGroupLabel?: string;
}

export interface IMeasureBuilderData {
  uid: string;
  measure: ICanBeChartedProperty;
  label?: string;
  reducer?: MeasureReducer;
  filters?: ComparableValue[];
}

export interface IGroupByDimensionMetadata {
  queryAttributes: string[];
  dataAccessor: DataAccessor;
  arrayDataAccessor?: ArrayDataAccessor;
  labelAccessor?: LabelAccessor;
  colourAccessor?: ColourAccessor;
}

export type ComparablePropertyNames<T> = {
  [K in keyof T]: T[K] extends ComparableValue ? K : never;
}[keyof T];
export type ComparableProperties<T> = Pick<T, ComparablePropertyNames<T>>;

export type MeasureTransformMap<Model> = {
  [K in keyof Model]: ICanBeChartedProperty;
};

export type IReportingProperty =
  | ICanBeChartedProperty
  | ICanGroupMeasuresProperty;

export type ReportingProperty =
  | ICanBeChartedProperty
  | ICanGroupMeasuresProperty;

export type CrossfilterGroup = Group<
  unknown,
  IGroupedDimension,
  NaturallyOrderedValue
>;

export type CrossfilterGroupValue = Grouping<
  IGroupedDimension,
  NaturallyOrderedValue
>;

export interface ICanBeChartedProperty {
  metadata: IMeasureMetadata;
  measure: IMeasure;
  clone: () => ICanBeChartedProperty;
  setMetadata(metadata: Partial<IMeasureMetadata>): ICanBeChartedProperty;
  setLabel(label: string): ICanBeChartedProperty;
  setIcon(icon: MeasureIcon): ICanBeChartedProperty;
  setFormatter(
    formatter: MeasureFormatter,
    formatterValue?: IMeasureMetadata['formatterValue']
  ): ICanBeChartedProperty;
  groupBy(groupBy: ICanGroupMeasuresProperty): ICanBeChartedProperty;
  filterBy(filter: IMeasureFilter, measure?: IMeasure): ICanBeChartedProperty;
  reduceBySum(): ICanBeChartedProperty;
  reduceByCount(): ICanBeChartedProperty;
  reduceByAverage(): ICanBeChartedProperty;
  reduceByMultiplier(multiplier: ICanBeChartedProperty): ICanBeChartedProperty;
  reduceByRatio(denominator?: ICanBeChartedProperty): ICanBeChartedProperty;
  reduceByCustom(reducerFn: ReduceByFunction): ICanBeChartedProperty;
  reduceBy(reducer: MeasureReducer): ICanBeChartedProperty;
}

export const isCanBeChartedProperty =
  TypeGuard.interface<ICanBeChartedProperty>({
    metadata: isObject,
    measure: isObject,
    clone: isFunction,
    setMetadata: isFunction,
    setLabel: isFunction,
    setIcon: isFunction,
    setFormatter: isFunction,
    groupBy: isFunction,
    filterBy: isFunction,
    reduceBySum: isFunction,
    reduceByCount: isFunction,
    reduceByAverage: isFunction,
    reduceByMultiplier: isFunction,
    reduceByRatio: isFunction,
    reduceByCustom: isFunction,
    reduceBy: isFunction,
  });

export interface ICanGroupMeasuresProperty {
  metadata: IMeasureMetadata;
  groupMeasure: IGroupByDimensionMetadata;
}

export const isCanGroupMeasuresProperty =
  TypeGuard.interface<ICanGroupMeasuresProperty>({
    metadata: isObject,
    groupMeasure: isObject,
  });

export type ICanDoAllProperty = ICanBeChartedProperty &
  ICanGroupMeasuresProperty;

export const isCanDoAllProperty = TypeGuard.merge(
  isCanBeChartedProperty,
  isCanGroupMeasuresProperty
);

export interface IMeasureRef {
  /**
   * Attribute path given to the query function.
   * Dot seperated path to the attribute.
   */
  attributePath: string;

  /**
   * Path to the property on the returned query data.
   * Underscore seperated path to the property
   */
  factPropertyPath: string;
}

export const isQueryBy = TypeGuard.interface<IMeasureRef>({
  attributePath: isString,
  factPropertyPath: isString,
});

export interface ICanQueryByTimestampProperty extends ICanDoAllProperty {
  queryBy: IMeasureRef;
}

export const isCanQueryByTimestampProperty = TypeGuard.merge(
  isCanDoAllProperty,
  TypeGuard.interface<
    Omit<ICanQueryByTimestampProperty, keyof ICanDoAllProperty>
  >({
    queryBy: isQueryBy,
  })
);

export interface IReduceByRate extends ComparableObject {
  count: number;
  numerator: number;
  denominator: number;
}

export interface IReduceByMultiplier extends ComparableObject {
  count: number;
  multiplicand: number;
  multiplier: number;
}
